/**
 * Creazione manuale documento commerciale (Fase 2.3).
 * Tipo (proforma/ricevuta/scontrino/...) da lookup configurabile. Emesso
 * (numerazione propria per tipo) o ricevuto (numero della controparte).
 * Controparte via Combobox searchable. Righe opzionali (uno scontrino puo'
 * avere solo il totale). UI solo da @floraltech/ui, nessun valore hardcoded.
 */
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  Input,
  Label,
} from '@floraltech/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Soggetto {
  id: string;
  ragione_sociale: string;
  partita_iva: string | null;
}

interface Classificazione {
  id: string;
  codice: string;
  descrizione: string;
}

interface Centro {
  id: string;
  codice: string;
  nome: string;
  attivo?: boolean;
}

interface RigaForm {
  descrizione: string;
  quantita: string;
  prezzo_unitario: string;
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

export function NuovoDocumento() {
  const navigate = useNavigate();
  const [tipi, setTipi] = useState<Classificazione[]>([]);
  const [tipo, setTipo] = useState('');
  const [direzione, setDirezione] = useState<'emesso' | 'ricevuto'>('ricevuto');
  const [controparteId, setControparteId] = useState('');
  const [controparteInput, setControparteInput] = useState('');
  const [soggetti, setSoggetti] = useState<Soggetto[]>([]);
  const [dataDoc, setDataDoc] = useState(new Date().toISOString().slice(0, 10));
  const [numeroControparte, setNumeroControparte] = useState('');
  const [causale, setCausale] = useState('');
  const [importoTotale, setImportoTotale] = useState('');
  const [centri, setCentri] = useState<Centro[]>([]);
  const [centroCostoId, setCentroCostoId] = useState('');

  const [righe, setRighe] = useState<RigaForm[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tipoSoggetto = direzione === 'emesso' ? 'cliente' : 'fornitore';

  useEffect(() => {
    void (async () => {
      const res = await fetch(
        '/api/v1/classificazioni?sistema=tipo_documento_commerciale',
        { headers: authHeaders() },
      );
      if (res.ok) {
        const data: Classificazione[] = await res.json();
        setTipi(data);
        if (data.length && !tipo) setTipo(data[0].codice);
      }
    })();
    void (async () => {
      const res = await fetch('/api/v1/centri-costo/centri?limit=200', {
        headers: authHeaders(),
      });
      if (res.ok) {
        const d = await res.json();
        setCentri(Array.isArray(d) ? d : (d.items ?? []));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cercaSoggetti = async (q: string) => {
    const res = await fetch(
      `/api/v1/soggetti?tipo=${tipoSoggetto}&q=${encodeURIComponent(q)}&limit=20`,
      { headers: authHeaders() },
    );
    if (res.ok) {
      const data = await res.json();
      setSoggetti(Array.isArray(data) ? data : (data.items ?? []));
    }
  };

  useEffect(() => {
    setControparteId('');
    setControparteInput('');
    setSoggetti([]);
    void cercaSoggetti('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direzione]);

  const setRiga = (i: number, campo: keyof RigaForm, val: string) => {
    setRighe((prev) => prev.map((r, idx) => (idx === i ? { ...r, [campo]: val } : r)));
  };
  const aggiungiRiga = () =>
    setRighe((prev) => [...prev, { descrizione: '', quantita: '1', prezzo_unitario: '0' }]);
  const rimuoviRiga = (i: number) => setRighe((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError(null);
    if (!tipo) {
      setError('Seleziona il tipo di documento');
      return;
    }
    if (!controparteId) {
      setError(`Seleziona un ${tipoSoggetto}`);
      return;
    }
    if (direzione === 'ricevuto' && !numeroControparte.trim()) {
      setError('Numero del documento della controparte obbligatorio per un documento ricevuto');
      return;
    }
    setBusy(true);
    try {
      const righePulite = righe
        .filter((r) => r.descrizione.trim())
        .map((r) => ({
          descrizione: r.descrizione.trim(),
          quantita: Number(r.quantita) || 1,
          prezzo_unitario: Number(r.prezzo_unitario) || 0,
        }));
      const payload = {
        tipo,
        direzione,
        controparte_id: controparteId,
        data_documento: dataDoc,
        numero_controparte: direzione === 'ricevuto' ? numeroControparte.trim() : null,
        causale: causale || null,
        centro_costo_id: centroCostoId || null,
        importo_totale:
          righePulite.length === 0 && importoTotale ? Number(importoTotale) : null,
        righe: righePulite,
      };
      const res = await fetch('/api/v1/documenti-commerciali', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail ?? `Errore ${res.status}`);
      }
      const data = await res.json();
      navigate(`/documenti-commerciali/${data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore creazione documento');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold">Nuovo documento</h1>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle>Testata</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Tipo documento</Label>
            <select
              className="w-full rounded border bg-transparent px-3 py-2 text-sm"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
            >
              {tipi.map((t) => (
                <option key={t.id} value={t.codice}>
                  {t.descrizione}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Direzione</Label>
            <select
              className="w-full rounded border bg-transparent px-3 py-2 text-sm"
              value={direzione}
              onChange={(e) => setDirezione(e.target.value as 'emesso' | 'ricevuto')}
            >
              <option value="ricevuto">Ricevuto (da fornitore)</option>
              <option value="emesso">Emesso (a cliente)</option>
            </select>
          </div>
          <div>
            <Label>Data documento</Label>
            <Input type="date" value={dataDoc} onChange={(e) => setDataDoc(e.target.value)} />
          </div>
          {direzione === 'ricevuto' && (
            <div>
              <Label>Numero documento controparte</Label>
              <Input
                value={numeroControparte}
                onChange={(e) => setNumeroControparte(e.target.value)}
                placeholder="Numero del documento del fornitore"
              />
            </div>
          )}
          <div className="md:col-span-2">
            <Label>{direzione === 'emesso' ? 'Cliente' : 'Fornitore'}</Label>
            <Combobox
              value={controparteInput}
              onInputChange={(v: string) => {
                setControparteInput(v);
                void cercaSoggetti(v);
              }}
              onSelect={(o) => {
                setControparteId(o.value);
                setControparteInput(o.label);
              }}
              options={soggetti.map((s) => ({
                value: s.id,
                label: s.ragione_sociale,
                sublabel: s.partita_iva ?? undefined,
              }))}
              placeholder={`Cerca ${tipoSoggetto} per nome o P.IVA...`}
            />
          </div>
          <div>
            <Label>Causale</Label>
            <Input
              value={causale}
              onChange={(e) => setCausale(e.target.value)}
              placeholder="Es. Acquisto al banco, Acconto, Saldo..."
            />
          </div>
          <div>
            <Label>Centro di costo</Label>
            <select
              className="w-full rounded border bg-transparent px-3 py-2 text-sm"
              value={centroCostoId}
              onChange={(e) => setCentroCostoId(e.target.value)}
            >
              <option value="">-</option>
              {centri
                .filter((c) => c.attivo !== false)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.codice} - {c.nome}
                  </option>
                ))}
            </select>
          </div>
          {righe.length === 0 && (
            <div>
              <Label>Importo totale (senza righe)</Label>
              <Input
                type="number"
                value={importoTotale}
                onChange={(e) => setImportoTotale(e.target.value)}
                placeholder="Totale del documento"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Righe (opzionali)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {righe.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <div className="col-span-7">
                <Input
                  placeholder="Descrizione"
                  value={r.descrizione}
                  onChange={(e) => setRiga(i, 'descrizione', e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  placeholder="Qta"
                  value={r.quantita}
                  onChange={(e) => setRiga(i, 'quantita', e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  placeholder="Prezzo"
                  value={r.prezzo_unitario}
                  onChange={(e) => setRiga(i, 'prezzo_unitario', e.target.value)}
                />
              </div>
              <div className="col-span-1 flex items-center">
                <button
                  type="button"
                  className="text-sm text-destructive"
                  onClick={() => rimuoviRiga(i)}
                >
                  X
                </button>
              </div>
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm" onClick={aggiungiRiga}>
            + Aggiungi riga
          </Button>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button onClick={submit} disabled={busy}>
          {busy ? 'Salvataggio...' : 'Crea documento'}
        </Button>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          Annulla
        </Button>
      </div>
    </div>
  );
}

export default NuovoDocumento;
